import React, { useState } from 'react';
import { Target, TrendingUp, Camera, Share2, CheckCircle, Megaphone, XCircle } from 'lucide-react';
import PageLayout, { PageHero, PageSection, PageCTA } from '../components/common/PageLayout';
import SEOHead from '../components/common/SEOHead';
import { useLanguage } from '../i18n/LanguageContext';

const BRAND_GALLERY = [
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/2xztpazu_0CDB6C67-7F8E-4A0A-A127-873DB57E2172.png', title: 'Activación de marca' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/6lghk188_6D5BE1DD-A003-4661-8A21-A95E8DAF5F00.png', title: 'Logo en el cielo' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/rvc6k1k9_D9E80893-54A2-4E3E-8B6C-CF39D72069E1.png', title: 'Lanzamiento de producto' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/0aeqa8md_751D6A44-4C45-41C2-9CC2-460CB22448CF.png', title: 'Evento corporativo' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/51aunvpj_77D8F1F5-F142-4E10-ACE9-DA7343FE1DE5.png', title: 'Inmobiliario' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/lc9lzn46_6F12EF87-F46F-4FFE-8533-A9B40475E226.png', title: 'Brand activation' },
];

const benefitIcons = [Target, TrendingUp, Camera, Share2];
const benefitKeys = ['highImpact', 'virality', 'premiumContent', 'amplification'];
const activationTypeKeys = ['productLaunch', 'openings', 'brandEvents', 'adCampaigns'];

const ActivacionesDeMarcaPage = () => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const { t, language } = useLanguage();
  const page = t('seoPages.brandActivations');

  return (
    <PageLayout>
      <SEOHead 
        title={language === 'es' ? "Activaciones de Marca con Drones | Marketing Aéreo | NexAir México" : "Brand Activations with Drones | Aerial Marketing | NexAir Mexico"}
        description={language === 'es' ? "Activaciones de marca espectaculares con shows de drones en México. Tu logo en el cielo, lanzamientos de producto y eventos corporativos que generan viralidad." : "Spectacular brand activations with drone shows in Mexico. Your logo in the sky, product launches and corporate events that generate virality."}
        keywords={language === 'es' ? "activacion de marca drones, marketing con drones, logo en el cielo, lanzamiento producto drones, evento corporativo drones, publicidad drones" : "brand activation drones, marketing with drones, logo in the sky, product launch drones, corporate event drones, advertising drones"}
        canonicalUrl="https://nexair.com.mx/activaciones-de-marca"
      />
      
      <PageHero 
        image="https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/2xztpazu_0CDB6C67-7F8E-4A0A-A127-873DB57E2172.png"
        title={page.title}
        subtitle={page.subtitle}
        description={page.description}
      />
      
      {/* Benefits */}
      <PageSection className="bg-[#030810]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
          {page.marketingTitle} <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">{page.marketingHighlight}</span>
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefitKeys.map((key, index) => {
            const Icon = benefitIcons[index];
            return (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-cyan-500/50 transition-all text-center">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-cyan-500/20 to-blue-500/20 flex items-center justify-center mb-4 mx-auto">
                  <Icon className="w-7 h-7 text-cyan-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{page.benefits[key].title}</h3>
                <p className="text-gray-400">{page.benefits[key].desc}</p>
              </div>
            );
          })}
        </div>
      </PageSection>
      
      {/* What We Offer */}
      <PageSection className="bg-[#050a15]">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {page.whatIncludesTitle}
            </h2>
            <p className="text-gray-300 text-lg mb-6">
              {page.whatIncludesDesc}
            </p>
            <div className="space-y-4">
              {page.whatIncludesList.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                  <span className="text-gray-300">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/rvc6k1k9_D9E80893-54A2-4E3E-8B6C-CF39D72069E1.png"
              alt={page.whatIncludesTitle}
              className="rounded-2xl border border-white/10 w-full"
              loading="lazy"
            />
          </div>
        </div>
      </PageSection>
      
      {/* Activation Types */}
      <PageSection className="bg-[#030810]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
          {page.activationTypesTitle}
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          {activationTypeKeys.map((key, index) => {
            const activationType = page.activationTypes[key];
            return (
              <div key={index} className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:border-cyan-500/30 transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <Megaphone className="w-6 h-6 text-cyan-400" />
                  <h3 className="text-xl font-bold text-white">{activationType.title}</h3>
                </div>
                <p className="text-gray-300 mb-4">{activationType.desc}</p>
                <div className="text-sm text-cyan-400">
                  <span className="text-gray-500">{page.idealForLabel}</span> {activationType.ideal}
                </div>
              </div>
            );
          })}
        </div>
      </PageSection>
      
      {/* Gallery */}
      <PageSection className="bg-[#050a15]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 text-center">
          {page.galleryTitle}
        </h2>
        <p className="text-gray-400 text-center mb-8 max-w-2xl mx-auto">
          {page.galleryDesc}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {BRAND_GALLERY.map((image, index) => (
            <div 
              key={index}
              className="aspect-video rounded-xl overflow-hidden border border-white/10 hover:border-cyan-500/50 transition-all cursor-pointer"
              onClick={() => setLightboxImage(image)}
            >
              <img 
                src={image.url} 
                alt={image.title}
                loading="lazy"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          ))}
        </div>
      </PageSection>
      
      {/* ROI Section */}
      <PageSection className="bg-[#030810]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
            {page.roiTitle}
          </h2>
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="text-4xl font-bold text-cyan-400 mb-2">{page.roiStats.reach.value}</div>
              <p className="text-gray-400">{page.roiStats.reach.desc}</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-cyan-400 mb-2">{page.roiStats.sharing.value}</div>
              <p className="text-gray-400">{page.roiStats.sharing.desc}</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-cyan-400 mb-2">{page.roiStats.impressions.value}</div>
              <p className="text-gray-400">{page.roiStats.impressions.desc}</p>
            </div>
          </div>
          <p className="text-gray-300 text-lg">
            {page.roiDesc}
          </p>
        </div>
      </PageSection>
      
      <PageCTA 
        title={page.ctaTitle}
        subtitle={page.ctaSubtitle}
        whatsappMessage={page.ctaWhatsapp}
      />

      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            <XCircle size={40} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img 
              src={lightboxImage.url} 
              alt={lightboxImage.title}
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
            />
            <div className="mt-4 text-center">
              <h3 className="text-white text-xl font-bold">{lightboxImage.title}</h3>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default ActivacionesDeMarcaPage;
